import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Archive, Building2, CheckCircle2, Cloud, Download, Gauge, LogIn,
         Plus, Receipt, RefreshCw, ShieldCheck, Timer, UserMinus, UserPlus, Users,
         XCircle } from "lucide-react";
import api, { apiError, setToken } from "@/lib/crmApi";
import { PageHeader, Loader, Empty } from "@/crm/ui";
import { useAuth } from "@/context/CrmAuth";
import { useOrganization } from "@/context/Organization";

/**
 * Organisation : identité, membres, plan et consommation (plan V4 §21, §33, §41).
 *
 * L'écran n'est PAS une frontière de sécurité : il cache les actions que le
 * membre n'a pas le droit de faire (`can(...)`) pour éviter des boutons qui
 * répondent 403, mais c'est le backend qui refuse (§11). Un membre qui
 * afficherait l'écran de force n'obtiendrait rien de plus.
 *
 * Il complète « Équipe » sans le remplacer : Équipe gère les COMPTES (rôle CRM,
 * secteurs, mot de passe), cet écran gère les APPARTENANCES (qui a le droit
 * d'entrer dans l'organisation, et avec quel rôle d'organisation).
 */
const FIELD = "w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/15";
const CARD = "rounded-xl border border-slate-200 bg-white p-5";
// Même masque que le backend (crm/tenancy/platform_integrations.SECRET_MASK) :
// une clé enregistrée est renvoyée masquée, jamais en clair. Le front n'en
// déduit qu'une chose : une clé existe.
const SECRET_MASK = "********";

const ROLE_LABEL = {
  OWNER: "Propriétaire",
  ADMIN: "Administrateur",
  MEMBER: "Membre",
  VIEWER: "Lecteur",
};

const METRIC_LABEL = {
  companies: "Entreprises",
  enrichment_jobs: "Enrichissements (jour)",
  provider_calls: "Appels sources (jour)",
  web_requests: "Requêtes web (jour)",
  ai_tokens: "Jetons IA (mois)",
  ai_cost_eur: "Coût IA (mois, €)",
  ai_runs: "Analyses IA (mois)",
  users: "Comptes",
  storage_mb: "Stockage (Mo)",
};

const LIMIT_FOR = {
  companies: "max_companies",
  enrichment_jobs: "max_enrichment_jobs_per_day",
  provider_calls: "max_provider_calls_per_day",
  web_requests: "max_web_requests_per_day",
  ai_tokens: "max_ai_tokens_per_month",
  ai_cost_eur: "monthly_ai_budget_eur",
  users: "max_users",
  storage_mb: "max_storage_mb",
};

// Les collections portent des noms techniques : l'écran d'un client parle de ce
// qu'il conserve, pas du schéma MongoDB.
const RETENTION_LABEL = {
  enrichment_evidence: "Preuves d'enrichissement",
  enrichment_field_history: "Historique des champs enrichis",
  ai_enrichment_runs: "Analyses IA (détail)",
  ai_enrichment_jobs: "Jobs d'enrichissement IA",
  enrichment_jobs: "Jobs d'enrichissement",
  enrichment_batches: "Lots d'enrichissement",
  telephony_events: "Événements opérateur",
  telephony_ops_logs: "Journal d'exploitation téléphonie",
  audit_logs: "Journal d'audit des fiches",
  system_activities: "Activités système",
};

const STATUS_BADGE = {
  ACTIVE: "bg-emerald-100 text-emerald-700",
  TRIAL: "bg-sky-100 text-sky-700",
  SUSPENDED: "bg-amber-100 text-amber-800",
  CANCELLED: "bg-slate-200 text-slate-700",
  DELETION_REQUESTED: "bg-rose-100 text-rose-700",
};

export default function OrganizationSettings() {
  const { user } = useAuth();
  const { current, can, permissions, reload } = useOrganization();
  const [members, setMembers] = useState(null);
  const [usage, setUsage] = useState(null);
  const [nom, setNom] = useState("");
  const [nouveau, setNouveau] = useState({ email: "", name: "", password: "", role: "MEMBER", crm_role: "commercial" });
  const [busy, setBusy] = useState(false);
  const [releve, setReleve] = useState(null);
  const [retention, setRetention] = useState(null);
  const [exports, setExports] = useState([]);
  const [parc, setParc] = useState(null);            // toutes les organisations
  const [creation, setCreation] = useState({ name: "", owner_email: "", owner_password: "", owner_name: "" });
  // Clés d'API de PLATEFORME (Google Cloud…) — repli pour toutes les
  // organisations, saisies ici par l'administrateur de plateforme.
  const [plateforme, setPlateforme] = useState(null);       // vue serveur, masquée
  const [saisiePlateforme, setSaisiePlateforme] = useState(""); // nouvelle clé tapée
  const [testPlateforme, setTestPlateforme] = useState(null);   // résultat du test
  const [testEnCours, setTestEnCours] = useState(false);

  const orgId = current?.id;

  const charger = useCallback(async () => {
    if (!orgId) return;
    setNom(current.name || "");
    // Chaque bloc est facultatif : un membre sans droit de gestion voit
    // l'identité de son organisation, pas la liste de ses collègues.
    if (can("members:manage")) {
      try {
        const { data } = await api.get(`/organizations/${orgId}/members`);
        setMembers(data.items || []);
      } catch { setMembers([]); }
    } else setMembers([]);
    if (can("costs:read")) {
      try {
        const [u, b] = await Promise.all([
          api.get("/organizations/current/usage"),
          api.get("/organizations/current/billing"),
        ]);
        setUsage(u.data);
        setReleve(b.data);
      } catch { setUsage(null); }
    }
    if (can("organization:manage")) {
      try {
        const [r, e] = await Promise.all([
          api.get("/organizations/current/retention"),
          api.get("/organizations/current/exports"),
        ]);
        setRetention(r.data.retention);
        setExports(e.data.items || []);
      } catch { /* déploiement plus ancien : les blocs restent masqués */ }
    }
    // Parc complet : réservé aux administrateurs de PLATEFORME. Un 403 ici est
    // la réponse normale pour un admin d'organisation, pas une erreur.
    try {
      const { data } = await api.get("/organizations/all");
      setParc(data.items || []);
    } catch { setParc(null); return; }
    // Clés de plateforme : même privilège que le parc. Un déploiement plus
    // ancien répond 404 : la carte reste masquée.
    try {
      const { data } = await api.get("/organizations/platform/integrations");
      setPlateforme(data);
      setSaisiePlateforme("");
    } catch { setPlateforme(null); }
  }, [orgId, current, can]);

  useEffect(() => { charger(); }, [charger]);

  if (!current) return <Loader label="Chargement de l'organisation…" />;

  const renommer = async () => {
    setBusy(true);
    try {
      await api.patch(`/organizations/${orgId}`, { name: nom });
      toast.success("Organisation mise à jour");
      await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const ajouter = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/organizations/${orgId}/members`, nouveau);
      toast.success("Membre ajouté");
      setNouveau({ email: "", name: "", password: "", role: "MEMBER", crm_role: "commercial" });
      charger();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const changerRole = async (userId, role) => {
    try {
      await api.patch(`/organizations/${orgId}/members/${userId}`, { role });
      charger();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const retirer = async (userId) => {
    try {
      await api.delete(`/organizations/${orgId}/members/${userId}`);
      toast.success("Appartenance retirée");
      charger();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const enregistrerRetention = async () => {
    setBusy(true);
    try {
      await api.put("/organizations/current/retention", { retention });
      toast.success("Rétention enregistrée");
      charger();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const apercuRetention = async () => {
    try {
      const { data } = await api.post("/organizations/current/retention/apply?dry_run=true");
      const total = data.total || 0;
      toast.info(total
        ? `${total} document(s) seraient effacés : ${Object.entries(data.deleted).map(([c, n]) => `${c} (${n})`).join(", ")}`
        : "Rien à effacer avec cette politique.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const exporter = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/organizations/${orgId}/export`);
      toast.success(`Export prêt : ${Object.values(data.counts).reduce((a, b) => a + b, 0)} documents`);
      charger();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Un `<a href>` n'enverrait pas l'en-tête Authorization : la route répondrait
  // 401. On récupère donc le fichier via axios (qui porte le jeton) et on
  // déclenche le téléchargement depuis le blob obtenu.
  const telecharger = async (nom) => {
    try {
      const { data } = await api.get(
        `/organizations/current/exports/${encodeURIComponent(nom)}`,
        { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const lien = document.createElement("a");
      lien.href = url;
      lien.download = nom;
      lien.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const demanderSuppression = async () => {
    if (!window.confirm(
      `Demander la suppression de « ${current.name} » ?\n\n`
      + "Rien n'est effacé maintenant : l'organisation passe en attente et "
      + "l'opération reste annulable. Exportez vos données d'abord.")) return;
    try {
      const { data } = await api.delete(`/organizations/${orgId}`);
      toast.warning(data.message);
      await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const annulerSuppression = async () => {
    try {
      await api.post(`/organizations/${orgId}/deletion/cancel`);
      toast.success("Suppression annulée");
      await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const creerOrganisation = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/organizations", {
        name: creation.name,
        ...(creation.owner_email
          ? { owner_email: creation.owner_email, owner_password: creation.owner_password,
              owner_name: creation.owner_name || creation.owner_email }
          : {}),
      });
      toast.success(`Organisation « ${data.organization.name} » créée`);
      setCreation({ name: "", owner_email: "", owner_password: "", owner_name: "" });
      charger();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const entrerDans = async (org) => {
    // Entrer chez un client dont on n'est pas membre est un geste privilégié :
    // le backend exige un motif et l'écrit dans le journal d'audit DU CLIENT
    // (plan V4 §46). Le demander ici plutôt que de laisser le 400 surprendre.
    const motif = org.is_mine ? "" : window.prompt(
      `Entrer dans « ${org.name} » sans en être membre.\n\n`
      + "Cet accès est journalisé chez le client. Motif (support, incident…) :");
    if (!org.is_mine && (!motif || motif.trim().length < 5)) return;
    try {
      const { data } = await api.post(`/organizations/${org.id}/switch`,
                                      motif ? { reason: motif } : {});
      setToken(data.token);
      window.location.reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const enregistrerPlateforme = async () => {
    if (!saisiePlateforme.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.put("/organizations/platform/integrations",
                                     { google_places_api_key: saisiePlateforme.trim() });
      setPlateforme(data);
      setSaisiePlateforme("");
      setTestPlateforme(null);
      toast.success("Clé Google Cloud de la plateforme enregistrée");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Échec de l'enregistrement"); }
    finally { setBusy(false); }
  };

  const retirerClePlateforme = async () => {
    if (!window.confirm("Retirer la clé Google Cloud de la plateforme ?\n\n"
      + "Les organisations sans clé propre perdront la fiche Google et les campagnes Google Places.")) return;
    setBusy(true);
    try {
      // `null` = retrait explicite ; une chaîne vide laisserait la clé en place.
      const { data } = await api.put("/organizations/platform/integrations", { google_places_api_key: null });
      setPlateforme(data);
      setSaisiePlateforme("");
      setTestPlateforme(null);
      toast.success("Clé Google Cloud de la plateforme retirée");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Échec du retrait"); }
    finally { setBusy(false); }
  };

  const testerClePlateforme = async () => {
    setTestEnCours(true);
    try {
      const { data } = await api.post("/organizations/platform/integrations/test-places");
      setTestPlateforme(data);
      if (data.ok) toast.success("Clé Google Cloud valide");
      else toast.error(data.error || "Échec du test");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Échec du test"); }
    finally { setTestEnCours(false); }
  };

  const clePlateformeEnregistree = plateforme?.google_places_api_key === SECRET_MASK;

  const plan = current.plan || {};
  const enSuppression = ["DELETION_REQUESTED", "SOFT_DELETED"].includes(current.status);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Organisation"
        subtitle="Identité, membres, plan et consommation de votre organisation"
      />

      {/* Identité */}
      <div className={CARD} data-testid="org-identity">
        <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Building2 size={16} className="text-indigo-600" /> Identité
          <span className={`ml-auto rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_BADGE[current.status] || "bg-slate-100 text-slate-700"}`}>
            {current.status}
          </span>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-500">Nom</span>
            <input className={FIELD} value={nom} onChange={(e) => setNom(e.target.value)}
              disabled={!can("organization:manage")} data-testid="org-name" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-500">Identifiant court</span>
            <input className={`${FIELD} bg-slate-50 text-slate-500`} value={current.slug || ""} readOnly />
          </label>
        </div>
        {can("organization:manage") && (
          <button type="button" onClick={renommer} disabled={busy || !nom.trim()}
            className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            Enregistrer
          </button>
        )}
      </div>

      {/* Plan et consommation */}
      {can("costs:read") && (
        <div className={CARD} data-testid="org-usage">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Gauge size={16} className="text-indigo-600" /> Plan « {plan.name || "standard"} » et consommation
          </div>
          {!usage ? <Loader label="Chargement…" /> : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Object.entries(usage.usage || {}).map(([metric, valeur]) => {
                const limite = plan[LIMIT_FOR[metric]];
                const illimite = limite === null || limite === undefined || limite === "";
                const pct = illimite ? 0 : Math.min(100, Math.round((valeur / Number(limite)) * 100));
                return (
                  <div key={metric} className="rounded-lg border border-slate-200 p-3">
                    <div className="text-xs text-slate-500">{METRIC_LABEL[metric] || metric}</div>
                    <div className="mt-0.5 text-lg font-semibold text-slate-800">
                      {Math.round(valeur * 100) / 100}
                      {!illimite && <span className="ml-1 text-sm font-normal text-slate-400">/ {limite}</span>}
                    </div>
                    {illimite
                      ? <div className="mt-1 text-[11px] text-slate-400">Sans limite</div>
                      : (
                        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
                          <div className={`h-full rounded-full ${pct >= 90 ? "bg-rose-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500"}`}
                            style={{ width: `${pct}%` }} />
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Membres */}
      {can("members:manage") && (
        <div className={CARD} data-testid="org-members">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Users size={16} className="text-indigo-600" /> Membres
            <span className="ml-auto text-xs font-normal text-slate-500">
              {(members || []).length} appartenance(s)
            </span>
          </div>

          {members === null ? <Loader /> : members.length === 0 ? (
            <Empty label="Aucun membre" icon={Users} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                    <th className="py-2 pr-3 font-medium">Membre</th>
                    <th className="py-2 pr-3 font-medium">Rôle CRM</th>
                    <th className="py-2 pr-3 font-medium">Rôle organisation</th>
                    <th className="py-2 pr-3 font-medium">Statut</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} className="border-b border-slate-100 last:border-0">
                      <td className="py-2.5 pr-3">
                        <div className="font-medium text-slate-800">{m.user?.name || m.name || "—"}</div>
                        <div className="text-xs text-slate-500">{m.user?.email || m.email}</div>
                      </td>
                      <td className="py-2.5 pr-3 text-slate-600">{m.user?.role || m.crm_role || "—"}</td>
                      <td className="py-2.5 pr-3">
                        <select
                          value={m.role}
                          onChange={(e) => changerRole(m.user_id, e.target.value)}
                          className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
                          aria-label={`Rôle de ${m.user?.name || m.email}`}
                        >
                          {Object.entries(ROLE_LABEL).map(([v, l]) => (
                            <option key={v} value={v}>{l}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2.5 pr-3 text-slate-600">{m.status}</td>
                      <td className="py-2.5 text-right">
                        {m.user_id !== user?.id && (
                          <button type="button" onClick={() => retirer(m.user_id)}
                            title="Retirer l'appartenance (le compte est conservé)"
                            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600">
                            <UserMinus size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <form onSubmit={ajouter} className="mt-5 grid gap-3 border-t border-slate-200 pt-5 sm:grid-cols-2 lg:grid-cols-5">
            <input className={FIELD} type="email" required placeholder="email@client.fr"
              value={nouveau.email} onChange={(e) => setNouveau({ ...nouveau, email: e.target.value })} />
            <input className={FIELD} placeholder="Nom complet"
              value={nouveau.name} onChange={(e) => setNouveau({ ...nouveau, name: e.target.value })} />
            <input className={FIELD} type="password" required placeholder="Mot de passe"
              value={nouveau.password} onChange={(e) => setNouveau({ ...nouveau, password: e.target.value })} />
            <select className={FIELD} value={nouveau.role}
              onChange={(e) => setNouveau({ ...nouveau, role: e.target.value })} aria-label="Rôle d'organisation">
              {Object.entries(ROLE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button type="submit" disabled={busy}
              className="flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              <UserPlus size={15} /> Ajouter
            </button>
          </form>
        </div>
      )}

      {/* Parc des organisations — administrateur de PLATEFORME (plan V4 §45) */}
      {parc && (
        <div className={CARD} data-testid="org-platform">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <ShieldCheck size={16} className="text-indigo-600" /> Organisations de la plateforme
            <span className="ml-auto text-xs font-normal text-slate-500">{parc.length}</span>
          </div>
          <p className="mb-4 text-xs text-slate-500">
            Visible parce que votre compte est administrateur de la plateforme
            (<code className="rounded bg-slate-100 px-1">PLATFORM_ADMIN_EMAILS</code>).
            Entrer dans une organisation dont vous n'êtes pas membre exige un motif
            et laisse une trace dans le journal d'audit du client.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                  <th className="py-2 pr-3 font-medium">Organisation</th>
                  <th className="py-2 pr-3 font-medium">Statut</th>
                  <th className="py-2 pr-3 font-medium">Plan</th>
                  <th className="py-2 pr-3 text-right font-medium">Membres</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {parc.map((o) => (
                  <tr key={o.id} className="border-b border-slate-100 last:border-0">
                    <td className="py-2.5 pr-3">
                      <div className="font-medium text-slate-800">{o.name}</div>
                      <div className="text-xs text-slate-500">{o.slug}</div>
                    </td>
                    <td className="py-2.5 pr-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[o.status] || "bg-slate-100 text-slate-700"}`}>
                        {o.status}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 text-slate-600">{o.plan}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums text-slate-600">{o.members}</td>
                    <td className="py-2.5 text-right">
                      {o.id === current.id ? (
                        <span className="text-xs text-slate-400">organisation active</span>
                      ) : o.status === "DELETED" ? null : (
                        <button type="button" onClick={() => entrerDans(o)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
                          <LogIn size={13} /> {o.is_mine ? "Basculer" : "Entrer"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <form onSubmit={creerOrganisation} className="mt-5 border-t border-slate-200 pt-5">
            <div className="mb-2 text-xs font-medium text-slate-500">
              Créer une organisation — le compte propriétaire est facultatif, mais
              sans lui personne ne pourra s'y connecter.
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <input className={FIELD} required placeholder="Nom du client" value={creation.name}
                data-testid="new-org-name"
                onChange={(e) => setCreation({ ...creation, name: e.target.value })} />
              <input className={FIELD} type="email" placeholder="email du propriétaire"
                value={creation.owner_email}
                onChange={(e) => setCreation({ ...creation, owner_email: e.target.value })} />
              <input className={FIELD} placeholder="nom du propriétaire" value={creation.owner_name}
                onChange={(e) => setCreation({ ...creation, owner_name: e.target.value })} />
              <input className={FIELD} type="password" placeholder="mot de passe"
                value={creation.owner_password}
                onChange={(e) => setCreation({ ...creation, owner_password: e.target.value })} />
              <button type="submit" disabled={busy || !creation.name.trim()}
                data-testid="new-org-submit"
                className="flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
                <Plus size={15} /> Créer
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Clés d'API de la plateforme — administrateur de PLATEFORME (plan V4 §41, §45) */}
      {parc && plateforme && (
        <div className={CARD} data-testid="org-platform-integrations">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Cloud size={16} className="text-indigo-600" /> Google Cloud — clé de la plateforme
          </div>
          <p className="mb-4 text-xs text-slate-500">
            Clé Google Cloud avec l'API « Places (New) » activée. Elle sert de repli à
            toutes les organisations qui n'ont pas saisi leur propre clé dans
            Paramètres › Intégrations (la clé d'une organisation prime toujours).
            Deux usages : la fiche Google Business à l'enrichissement, et les campagnes
            d'extraction « Google Places ». Chaque recherche est facturée par Google
            au-delà du quota gratuit, sur le projet de cette clé.
          </p>
          <div className="mb-3 flex items-center gap-2 text-xs" data-testid="platform-places-key-status">
            {clePlateformeEnregistree ? (
              <><CheckCircle2 size={14} className="shrink-0 text-emerald-600" />
                <span className="text-emerald-700">Une clé est enregistrée pour la plateforme.</span></>
            ) : (
              <><AlertTriangle size={14} className="shrink-0 text-amber-500" />
                <span className="text-amber-700">Aucune clé de plateforme : seules les organisations ayant leur propre clé
                  (ou la variable d'environnement <code className="rounded bg-slate-100 px-1">GOOGLE_PLACES_API_KEY</code>)
                  accèdent à Google Places.</span></>
            )}
          </div>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-500">
              {clePlateformeEnregistree ? "Remplacer la clé" : "Clé API Google Places"}
            </span>
            <input type="password" className={`${FIELD} font-mono`} autoComplete="off"
              placeholder={clePlateformeEnregistree ? "Saisir une nouvelle clé pour remplacer l'actuelle" : "AIza…"}
              data-testid="platform-places-api-key"
              value={saisiePlateforme}
              onChange={(e) => setSaisiePlateforme(e.target.value)} />
          </label>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button type="button" onClick={enregistrerPlateforme}
              disabled={busy || !saisiePlateforme.trim()}
              data-testid="platform-integrations-save"
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              Enregistrer
            </button>
            {clePlateformeEnregistree && (
              <button type="button" onClick={testerClePlateforme} disabled={busy || testEnCours}
                data-testid="platform-integrations-test"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
                <RefreshCw size={13} className={testEnCours ? "animate-spin" : ""} /> Tester la clé enregistrée
              </button>
            )}
            {clePlateformeEnregistree && (
              <button type="button" onClick={retirerClePlateforme} disabled={busy}
                data-testid="platform-integrations-remove"
                className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-60">
                <XCircle size={13} /> Retirer la clé
              </button>
            )}
          </div>
          {testPlateforme && (
            <div className={`mt-3 flex items-start gap-1.5 text-xs ${testPlateforme.ok ? "text-emerald-600" : "text-rose-600"}`}
              data-testid="platform-integrations-test-result">
              {testPlateforme.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <XCircle size={14} className="mt-0.5 shrink-0" />}
              <span>{testPlateforme.ok ? `Résultat : ${testPlateforme.sample}` : testPlateforme.error}</span>
            </div>
          )}
        </div>
      )}

      {/* Relevé de consommation (plan V4 §33) */}
      {can("costs:read") && releve && (
        <div className={CARD} data-testid="org-billing">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Receipt size={16} className="text-indigo-600" /> Relevé de consommation — {releve.period}
            <span className="ml-auto text-base font-semibold text-slate-800">
              {releve.total_eur.toFixed(2)} €
            </span>
          </div>
          <p className="mb-3 text-xs text-slate-500">{releve.notice}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                  <th className="py-2 pr-3 font-medium">Poste</th>
                  <th className="py-2 pr-3 text-right font-medium">Consommé</th>
                  <th className="py-2 pr-3 text-right font-medium">Inclus</th>
                  <th className="py-2 pr-3 text-right font-medium">Facturable</th>
                  <th className="py-2 text-right font-medium">Montant</th>
                </tr>
              </thead>
              <tbody>
                {releve.lines.filter((l) => l.quantity || l.amount_eur).map((l) => (
                  <tr key={l.metric} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 pr-3 text-slate-700">{METRIC_LABEL[l.metric] || l.metric}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{l.quantity} <span className="text-slate-400">{l.unit}</span></td>
                    <td className="py-2 pr-3 text-right tabular-nums text-slate-500">{l.included ?? "illimité"}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{l.billable}</td>
                    <td className="py-2 text-right tabular-nums font-medium">{l.amount_eur.toFixed(4)} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Rétention (plan V4 §47) */}
      {can("organization:manage") && retention && (
        <div className={CARD} data-testid="org-retention">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Timer size={16} className="text-indigo-600" /> Rétention des données
          </div>
          <p className="mb-4 text-xs text-slate-500">
            Durée de conservation, en jours. Vide = conservation illimitée, et c'est
            le réglage par défaut : la migration multi-tenant n'efface rien de ce
            qui était conservé auparavant.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(retention).map(([collection, jours]) => (
              <label key={collection} className="block">
                <span className="mb-1 block text-xs font-medium text-slate-500">
                  {RETENTION_LABEL[collection] || collection}
                </span>
                <input type="number" min="0" className={FIELD} placeholder="illimité"
                  value={jours ?? ""}
                  onChange={(e) => setRetention({
                    ...retention,
                    [collection]: e.target.value === "" ? null : Number(e.target.value),
                  })} />
              </label>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" onClick={enregistrerRetention} disabled={busy}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              Enregistrer
            </button>
            <button type="button" onClick={apercuRetention}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700">
              Simuler sans effacer
            </button>
          </div>
        </div>
      )}

      {/* Export et cycle de vie (plan V4 §48) */}
      {can("organization:manage") && (
        <div className={`${CARD} border-rose-200`} data-testid="org-danger">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-rose-700">
            <AlertTriangle size={16} /> Export et suppression
          </div>
          <p className="mb-4 text-xs text-slate-500">
            La suppression se fait en plusieurs étapes, toutes annulables jusqu'à la
            purge définitive : demande → suspension → rétention → purge. Exportez
            vos données avant de commencer.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={exporter} disabled={busy}
              className="flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 disabled:opacity-50">
              <Archive size={15} /> Exporter toutes les données
            </button>
            {enSuppression ? (
              <button type="button" onClick={annulerSuppression}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white">
                Annuler la suppression
              </button>
            ) : (
              <button type="button" onClick={demanderSuppression}
                className="rounded-lg border border-rose-300 px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50">
                Demander la suppression de l'organisation
              </button>
            )}
          </div>
          {exports.length > 0 && (
            <ul className="mt-4 space-y-1 border-t border-slate-200 pt-4">
              {exports.map((f) => (
                <li key={f.name} className="flex items-center gap-2 text-sm">
                  <Download size={14} className="shrink-0 text-slate-400" />
                  <button type="button" onClick={() => telecharger(f.name)}
                    className="truncate text-indigo-600 hover:underline">{f.name}</button>
                  <span className="ml-auto shrink-0 text-xs text-slate-400">
                    {Math.round(f.size / 1024)} Ko
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Permissions du membre connecté */}
      <div className={CARD} data-testid="org-permissions">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
          <ShieldCheck size={16} className="text-indigo-600" /> Vos permissions dans cette organisation
        </div>
        <p className="mb-3 text-xs text-slate-500">
          Ces permissions sont appliquées côté serveur. Cette liste est indicative :
          elle sert à masquer les actions indisponibles, pas à les autoriser.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {(permissions || []).map((p) => (
            <span key={p} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700">{p}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
